import React, { useState } from 'react';
import { Button, Input, Box, Typography, TextField } from '@mui/material';
import * as XLSX from 'xlsx';

const JsonToExcel: React.FC = () => {
  const [ready, setReady] = useState(false);
  const [sheets, setSheets] = useState<Record<string, any[]>>({});
  const [fileName, setFileName] = useState('export');
  const [includeOptions, setIncludeOptions] = useState(true);
  const [optionKeys, setOptionKeys] = useState('choices');

  const flattenObject = (
    obj: any,
    includeOptions: boolean,
    optionFields: string[] = [],
    prefix = ''
  ): Record<string, any> => {
    let result: Record<string, any> = {};
  
    for (const key in obj) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];
  
      // Handle array field that may need Option A–D
      if (optionFields.includes(key) && Array.isArray(value)) {
        result[newKey] = value.join(', ');
      
        if (includeOptions) {
          result['Option A'] = value[0] ?? '';
          result['Option B'] = value[1] ?? '';
          result['Option C'] = value[2] ?? '';
          result['Option D'] = value[3] ?? '';
      
          // Add Markdown-formatted string as a new column
          result['Formatted Options'] = `**Option A:** ${value[0] ?? ''}  \n**Option B:** ${value[1] ?? ''}  \n**Option C:** ${value[2] ?? ''}  \n**Option D:** ${value[3] ?? ''}`;
        }
      }
      
  
      // Handle other arrays
      else if (Array.isArray(value)) {
        if (value.length && typeof value[0] === 'object') {
          value.forEach((item, index) => {
            Object.assign(
              result,
              flattenObject(item, includeOptions, optionFields, `${newKey}[${index}]`)
            );
          });
        } else {
          result[newKey] = value.join(', ');
        }
      }
  
      // Handle nested objects
      else if (typeof value === 'object' && value !== null) {
        Object.assign(result, flattenObject(value, includeOptions, optionFields, newKey));
      }
  
      // Handle primitive values
      else {
        result[newKey] = value;
      }
    }
  
    return result;
  };

  const findAllObjectArrays = (obj: any, path = 'root', results: Record<string, any[]> = {}) => {
    if (Array.isArray(obj) && obj.length > 0 && typeof obj[0] === 'object') {
      results[path] = obj;
    } else if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        findAllObjectArrays(obj[key], `${path}.${key}`, results);
      }
    }
    return results;
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setFileName(nameWithoutExt);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const content = evt.target?.result as string;
        const json = JSON.parse(content);
        const optionFields = optionKeys.split(',').map(s => s.trim());

        const allSheets = findAllObjectArrays(json);
        const flattenedSheets: Record<string, any[]> = {};

        for (const [sheetName, data] of Object.entries(allSheets)) {
          flattenedSheets[sheetName] = data.map((item) =>
            flattenObject(item, includeOptions, optionFields)
          );
        }

        if (Object.keys(flattenedSheets).length === 0) {
          alert('⚠️ No array of objects found in this JSON.');
          return;
        }

        setSheets(flattenedSheets);
        setReady(true);
      } catch (err) {
        console.error(err);
        alert('❌ Failed to parse JSON.');
        setReady(false);
      }
    };

    reader.readAsText(file);
  };

  const handleDownload = () => {
    const workbook = XLSX.utils.book_new();
    for (const [sheetName, data] of Object.entries(sheets)) {
      const safeName = sheetName.length > 31 ? sheetName.slice(-31) : sheetName;
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
    }
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        📁 Dynamic JSON → Excel Exporter
      </Typography>

      <Input type="file" inputProps={{ accept: '.json' }} onChange={handleUpload} sx={{ my: 2 }} />

      <Box mb={2}>
        <label>
          <input
            type="checkbox"
            checked={includeOptions}
            onChange={(e) => setIncludeOptions(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Include Option A–D from specified fields
        </label>
      </Box>

      <TextField
        label="Fields to extract options from"
        variant="outlined"
        size="small"
        fullWidth
        value={optionKeys}
        onChange={(e) => setOptionKeys(e.target.value)}
        helperText='Comma-separated keys like: "choices,answers"'
        sx={{ mb: 2 }}
      />

      <Button variant="contained" onClick={handleDownload} disabled={!ready}>
        Download Excel
      </Button>
    </Box>
  );
};

export default JsonToExcel;